import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';
import cx from 'classnames';
import s from './Password.module.css';

const useStyles = makeStyles(() => ({
    root: {
        '& .MuiFormControl-root.MuiTextField-root': {
            marginBottom: '30px !important',
        },
        '& .MuiFilledInput-input:-webkit-autofill': {
            borderRadius: '6px !important',
        },
        '& .MuiFormLabel-root': {
            color: '#8692A6',
        },
        '& .MuiFilledInput-root': {
            backgroundColor: '#F7F7F7',
            borderRadius: '6px',
        },
        '& .MuiInputBase-root': {
            border: '1px solid transparent',
        },
        '& .MuiInputBase-root.Mui-error': {
            border: '1px solid #F90000',
        },
        '& .MuiInputBase-root:hover': {
            border: '1px solid #DCDCDC',
        },
        '& .MuiInputBase-root.Mui-focused:not(.Mui-error)': {
            border: '1px solid #1094F7',
        },
        '& .MuiTooltip-popper': {
            fontSize: '14px !important',
        },
        '& .MuiFormHelperText-root': {
            lineHeight: '16px',
            fontSize: 14,
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: '#F90000',
            lineHeight: '16px',
        },
        '& .MuiInputBase-root.Mui-error:hover': {
            border: '1px solid #F90000',
        },
    },
}));

const tooltipStyles = makeStyles((theme) => ({
    tooltip: {
        backgroundColor: '#343C44',
        color: '#FFFFFF',
        fontSize: '14px',
        border: '1px solid #dadde9',
        borderRadius: '6px',
        lineHeight: '16px',
        top: '8px !important',
        maxWidth: 414,
        padding: 10,
        [theme.breakpoints.down('xs')]: {
            maxWidth: 251,
        },
    },
    redTooltip: {
        backgroundColor: '#FEE3E3',
        color: '#F90000',
        fontSize: '14px',
        border: '1px solid rgba(249, 0, 0, 0.23)',
        borderRadius: '6px',
        lineHeight: '16px',
        top: '8px !important',
        maxWidth: 414,
        padding: 10,
        [theme.breakpoints.down('xs')]: {
            maxWidth: 251,
        },
    },
    arrow: {
        color: '#343C44',
        left: '22px !important',
    },
    redArrow: {
        color: 'rgba(249, 0, 0, 0.11)',
        left: '22px !important',
        '&::before': {
            border: '1px solid rgb(255 183 180)',
            background: '#FEE3E3',
        },
    },
}));

export const PasswordInput = ({
    updatePassword,
    validatePassword,
    setErrors,
    errors,
    label,
    id,
    tooltip,
    passwordValue,
}) => {
    const [open, setIsOpen] = useState(false);
    const [isRedTooltip, setIsRedTooltip] = useState(false);
    const [value, setValue] = useState({
        password: '',
        isPasswordShown: false,
    });
    const classes = useStyles();
    const tooltipClasses = tooltipStyles();

    const closeTooltip = () => {
        setIsOpen(false);
    };

    const openTooltip = () => {
        setIsOpen(true);
    };

    const clickPasswordIcon = () => {
        setValue({ ...value, isPasswordShown: !value.isPasswordShown });
    };

    return (
        <Tooltip
            PopperProps={{
                disablePortal: true,
            }}
            classes={{
                arrow: isRedTooltip ? tooltipClasses.redArrow : tooltipClasses.arrow,
                tooltip: isRedTooltip ? tooltipClasses.redTooltip : tooltipClasses.tooltip,
            }}
            onClose={closeTooltip}
            open={open && tooltip}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={i18next.t('PasswordInput.Tooltip.title')}
            placement="top-start"
            arrow
        >
            <div>
                <TextField
                    label={label}
                    id={id}
                    data-id="password-register"
                    type={value.isPasswordShown ? 'text' : 'password'}
                    value={value.password || passwordValue}
                    classes={classes}
                    onChange={(e) => {
                        updatePassword(e.target.value);
                        const error = validatePassword(e.target.value);
                        setErrors((prevState) => ({ ...prevState, password: error }));
                        if (error) {
                            openTooltip();
                        } else {
                            closeTooltip();
                        }
                    }}
                    onBlur={(e) => {
                        const error = validatePassword(e.target.value);
                        setErrors((prevState) => ({ ...prevState, password: error }));
                        if (errors.password === ' ') {
                            setIsRedTooltip(true);
                            openTooltip();
                        } else {
                            setIsRedTooltip(false);
                            closeTooltip();
                        }
                    }}
                    onFocus={(e) => {
                        if (e.target.value.length === 0) {
                            openTooltip();
                        } else if (errors.password) {
                            openTooltip();
                        } else {
                            closeTooltip();
                        }
                    }}
                    error={!!errors.password}
                    helperText={errors.password ?? ' '}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment
                                onClick={clickPasswordIcon}
                                data-id="password-register-icon"
                                position="end"
                            >
                                <div className={s.showPasswordIcon} />
                                {value.isPasswordShown ? (
                                    <div className={cx(s.passwordIcon, s.showPasswordIcon)} />
                                ) : (
                                    <div className={cx(s.passwordIcon, s.hidePasswordIcon)} />
                                )}
                            </InputAdornment>
                        ),
                        disableUnderline: true,
                    }}
                    inputProps={{
                        'data-id': 'register-password',
                        autoFocus: false,
                    }}
                    variant="filled"
                />
            </div>
        </Tooltip>
    );
};
